import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  clearAuthDataAction,
  setData2faAction,
  setDataAndTokenAction,
  setForgotPasswordDataAction,
  setLoginSessionChallengeAction,
  setUserDetailAction,
} from '@/redux/slicers/auth.slice';
import { setMessageAction } from '@/redux/slicers/api-message.slice';
import { Messages } from '@/constants/message';
import { useRootSelector } from './selector.hook';
import {
  setToken,
  setRefreshToken,
  setTokenType,
  removeToken,
  removeRefreshToken,
  removeTokenType,
  getToken,
  getRefreshToken,
  isTokenExpired,
  isRefreshTokenExpired,
} from '@/utils/common';
import { useApi, useCaller } from '@/hooks/api.hook';
// import { findFirstSidebarRoute, normalizeSidebarRoute } from '@/utils/sidebar';
import type {
  ChangePasswordType,
  ForgotPasswordRequest,
  Login2FARequest,
  ResetForgotPasswordRequest,
  ResendForgotPasswordRequest,
  VerifyForgotPasswordRequest,
} from '@/types/auth';
import { resetSidebarAction } from '@/redux/slicers/sidebar.slice';
import { useSidebarByRole } from '@/services/sidebar.service';
import { refreshAccessToken, registerAuthFailureHandler } from '@/utils/auth-session';

const DEFAULT_AUTHENTICATED_ROUTE = '/admin/dashboard';
const DEFAULT_UNAUTHENTICATED_ROUTE = '/login';

// Global state để tránh duplicate profile fetch calls
let isFetchingProfile = false;
let profilePromise: Promise<any> | null = null;

const resolveAvatarViewUrl = async (api: any, caller: any, avatarFileId?: string | null) => {
  if (!avatarFileId) return undefined;

  try {
    const response = await caller(() => api.get(`/api/v1/files/${avatarFileId}/view`), {
      messageKey: '',
      loadingKey: `avatar-view-${avatarFileId}`,
    });

    const avatarUrl =
      (typeof response?.content === 'string' ? response.content : undefined) ||
      (response?.content?.content as string | undefined);

    return response?.succeeded ? avatarUrl : undefined;
  } catch (error) {
    console.error('Failed to resolve avatar view URL:', error);
    return undefined;
  }
};

const attachAvatarUrl = async <
  T extends { avatarFileId?: string; avatarUrl?: string; avatar?: string },
>(
  api: any,
  caller: any,
  profile?: T | null,
) => {
  if (!profile) return profile;

  const avatarUrl = await resolveAvatarViewUrl(api, caller, profile.avatarFileId);

  if (!avatarUrl) {
    return profile;
  }

  return {
    ...profile,
    avatarUrl,
  };
};

const extractAuthTokens = (content: any) => {
  const accessToken =
    content?.auth?.token?.accessToken ||
    content?.token?.accessToken ||
    content?.accessToken ||
    null;
  const refreshToken =
    content?.auth?.token?.refreshToken ||
    content?.token?.refreshToken ||
    content?.refreshToken ||
    null;
  const tokenType =
    content?.auth?.token?.tokenType || content?.token?.tokenType || content?.tokenType || null;

  return {
    accessToken,
    refreshToken,
    tokenType,
  };
};

// Global fetch profile function để đảm bảo chỉ gọi 1 lần
const globalFetchProfile = async (api: any, caller: any, dispatch: any, currentUser: any) => {
  // Nếu đã có user, không cần fetch
  if (currentUser) {
    return { success: true, user: currentUser };
  }

  // Nếu đang fetch, return promise hiện tại
  if (isFetchingProfile && profilePromise) {
    return profilePromise;
  }

  // Bắt đầu fetch
  isFetchingProfile = true;

  profilePromise = (async () => {
    try {
      const result = await caller(() => api.get('/api/v1/admin/users/me'), {
        loadingKey: 'profile-loading',
      });

      if (result?.succeeded) {
        const profileResponse = result.content?.user || result.content?.profile || result.content;
        const profile = await attachAvatarUrl(api, caller, profileResponse);

        dispatch(
          setDataAndTokenAction({
            user: profile,
          }),
        );
        return { success: true, user: profile };
      }

      return { success: false };
    } catch (error) {
      console.error('âŒ Global fetch profile error:', error);
      return { success: false };
    } finally {
      isFetchingProfile = false;
      profilePromise = null;
    }
  })();

  return profilePromise;
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const api = useApi('');
  const caller = useCaller();
  const navigate = useNavigate();
  const { getSidebarByRole } = useSidebarByRole();
  const { isAuthenticated, isLoading, user, forgotPasswordData } = useRootSelector(
    (state) => state.auth,
  );

  const clearAuthState = useCallback(() => {
    removeToken();
    removeRefreshToken();
    removeTokenType();

    dispatch(clearAuthDataAction());
    dispatch(resetSidebarAction());
  }, [dispatch]);

  const clearData = useCallback(async () => {
    clearAuthState();
    navigate(DEFAULT_UNAUTHENTICATED_ROUTE, { replace: true });
  }, [clearAuthState, navigate]);

  useEffect(() => {
    registerAuthFailureHandler(async () => {
      await clearData();
    });

    return () => {
      registerAuthFailureHandler(null);
    };
  }, [clearData]);

  const fetchProfile = useCallback(async () => {
    // Sử dụng global fetch function
    await globalFetchProfile(api, caller, dispatch, user);
  }, [api, caller, dispatch, user]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/sessions/login`, {
            email,
            password,
          }),
        {
          loadingKey: 'login-loading',
        },
      );

      if (succeeded) {
        if (content?.status === 2 && content?.challenge) {
          dispatch(
            setLoginSessionChallengeAction({
              status: 2,
              challenge: {
                challengeToken: content.challenge.challengeToken,
                expiresAtUtc: content.challenge.expiresAtUtc,
                destinationMasked: content.challenge.destinationMasked,
              },
            }),
          );
          return;
        }

        if (content?.status !== 1) {
          throw new Error('Unsupported login status');
        }

        dispatch(setLoginSessionChallengeAction(null));
        let { accessToken: token, refreshToken, tokenType } = extractAuthTokens(content);

        if (!token || !refreshToken) {
          throw new Error('Login response does not contain auth tokens');
        }

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);
        setTokenType(tokenType || 'Bearer');

        // Lưu factoryId và managerId nếu là manager vào localStorage
        // if (loginUser?.factoryId) {
        //   setFactoryId(loginUser.factoryId);
        // }
        // if (loginUser?.isManager && loginUser?.id) {
        //   setManagerId(loginUser.id);
        // }

        // Kiểm tra token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API sử dụng global refresh function
              const result = await refreshAccessToken();

              if (result.success) {
                // Refresh thành công -> lưu token mới vào localStorage
                token = result.accessToken;
                refreshToken = result.refreshToken;
                tokenType = result.tokenType || tokenType || 'Bearer';

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                setRefreshToken(refreshToken);
                setTokenType(tokenType);

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                    isAuthenticated: true,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                await clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              await clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            await clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
              isAuthenticated: true,
            }),
          );
        }

        // Sau khi có token hợp lệ, load profile và sidebar để render header/menu đúng dữ liệu user.
        await fetchProfile();
        await getSidebarByRole();

        dispatch(
          setMessageAction([
            'global',
            {
              type: 'success',
              messages: [Messages.LOGIN_SUCCESS],
            },
          ]),
        );

        navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
      }
    },
    [api, caller, clearData, dispatch, fetchProfile, navigate, getSidebarByRole],
  );

  const loginWith2FA = useCallback(
    async ({ challengeToken, code, recoveryCode }: Login2FARequest) => {
      const payload: Record<string, string> = { challengeToken };

      if (code) {
        payload.code = code;
      }

      if (recoveryCode) {
        payload.recoveryCode = recoveryCode;
      }

      const response = await caller(() => api.post(`/api/v1/auths/sessions/login/2fa`, payload), {
        loadingKey: 'login-2fa-loading',
      });

      if (!response?.succeeded || !response.content) {
        return {
          succeeded: false,
          content: response?.content ?? null,
        };
      }

      let { accessToken: token, refreshToken, tokenType } = extractAuthTokens(response.content);

      if (!token || !refreshToken) {
        throw new Error('2FA login response does not contain auth tokens');
      }

      setToken(token);
      setRefreshToken(refreshToken);
      setTokenType(tokenType || 'Bearer');

      if (token && isTokenExpired(token)) {
        if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
          try {
            const result = await refreshAccessToken();

            if (result.success) {
              token = result.accessToken;
              refreshToken = result.refreshToken;
              tokenType = result.tokenType || tokenType || 'Bearer';

              setToken(token);
              setRefreshToken(refreshToken);
              setTokenType(tokenType);

              dispatch(
                setDataAndTokenAction({
                  token,
                  refreshToken,
                  isAuthenticated: true,
                }),
              );
            } else {
              await clearData();
              return {
                succeeded: false,
                content: null,
              };
            }
          } catch (error) {
            console.error('Token refresh failed after 2FA login:', error);
            await clearData();
            return {
              succeeded: false,
              content: null,
            };
          }
        } else {
          await clearData();
          return {
            succeeded: false,
            content: null,
          };
        }
      } else {
        dispatch(
          setDataAndTokenAction({
            token,
            refreshToken,
            isAuthenticated: true,
          }),
        );
      }

      dispatch(setLoginSessionChallengeAction(null));

      await fetchProfile();
      await getSidebarByRole();

      dispatch(
        setMessageAction([
          'global',
          {
            type: 'success',
            messages: [Messages.LOGIN_SUCCESS],
          },
        ]),
      );

      navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });

      return {
        succeeded: true,
        content: response.content,
      };
    },
    [api, caller, clearData, dispatch, fetchProfile, getSidebarByRole, navigate],
  );

  const forgotPassword = useCallback(
    async ({ email }: ForgotPasswordRequest) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/forgot-password/send`, {
            email,
          }),
        {
          loadingKey: 'forgot-password-loading',
        },
      );

      if (succeeded && content) {
        dispatch(setForgotPasswordDataAction(content));
        navigate('/verify-code');
      }

      return {
        content,
        succeeded,
      };
    },
    [api, caller, dispatch, navigate],
  );

  const resendForgotPassword = useCallback(
    async ({ challengeToken, destinationMasked }: ResendForgotPasswordRequest) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/forgot-password/resend`, {
            challengeToken,
            destinationMasked,
          }),
        {
          loadingKey: 'forgot-password-resend-loading',
        },
      );

      if (succeeded && content) {
        dispatch(setForgotPasswordDataAction(content));
      }

      return {
        content,
        succeeded,
      };
    },
    [api, caller, dispatch],
  );

  const verifyForgotPassword = useCallback(
    async ({ challengeToken, otp }: VerifyForgotPasswordRequest) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/forgot-password/verify`, {
            challengeToken,
            otp,
          }),
        {
          loadingKey: 'forgot-password-verify-loading',
        },
      );

      if (succeeded && content) {
        dispatch(
          setForgotPasswordDataAction({
            ...(forgotPasswordData ?? {
              challengeToken,
              challengeExpiresAtUtc: content.expiresAtUtc,
              resendAvailableAtUtc: content.expiresAtUtc,
            }),
            status: content.status,
            expiresAtUtc: content.expiresAtUtc,
            destinationMasked: content.destinationMasked,
            challengeToken,
            resetToken: content.resetToken,
          }),
        );
        navigate('/reset-password');
      }

      return {
        content,
        succeeded,
      };
    },
    [api, caller, dispatch, forgotPasswordData, navigate],
  );

  const resetPassword = useCallback(
    async ({ resetToken, password, confirmPassword }: ResetForgotPasswordRequest) => {
      const { content, succeeded } = await caller(
        () =>
          api.post(`/api/v1/auths/forgot-password/reset`, {
            resetToken,
            password,
            confirmPassword,
          }),
        {
          loadingKey: 'forgot-password-reset-loading',
        },
      );

      if (succeeded) {
        dispatch(setForgotPasswordDataAction(null));
      }

      return {
        content,
        succeeded,
      };
    },
    [api, caller, dispatch],
  );

  const changePassword = useCallback(
    async ({ oldPassword, newPassword }: ChangePasswordType) => {
      const response = await caller(
        () =>
          api.post(`/api/v1/admin/users/me/password/change`, {
            oldPassword,
            newPassword,
          }),
        {
          loadingKey: 'change-password-loading',
          successMessage: 'Password updated successfully',
        },
      );

      return response;
    },
    [api, caller],
  );

  // DEPRECATED: refreshToken function removed
  // Axios interceptor now handles all token refresh automatically
  // This prevents duplicate refresh calls and race conditions

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = getToken();
      const refreshToken = getRefreshToken();

      // If there is no token, the user is not logged in
      if (!token) {
        clearAuthState();
        return false;
      }

      // If the token is still valid, the user is logged in
      if (!isTokenExpired(token)) {
        dispatch(
          setDataAndTokenAction({
            isAuthenticated: true,
            token,
            refreshToken,
          }),
        );

        // Fetch profile để load user info
        await fetchProfile();

        // Fetch sidebar để load menus khi reload
        await getSidebarByRole();

        return true;
      }

      // Token expired, check the refresh token
      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        clearAuthState();
        return false;
      }

      // Refresh token is still valid, use the global refresh function
      try {
        const result = await refreshAccessToken();

        if (result.success) {
          // Update Redux
          dispatch(
            setDataAndTokenAction({
              isAuthenticated: true,
              token: result.accessToken,
              refreshToken: result.refreshToken,
            }),
          );

          // Fetch profile sau khi refresh token thành công
          await fetchProfile();

          // Fetch sidebar để load menus khi reload
          await getSidebarByRole();

          return true;
        }

        // Refresh thất bại
        clearAuthState();
        return false;
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        clearAuthState();
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      clearAuthState();
      return false;
    }
  }, [api, caller, clearAuthState, dispatch, fetchProfile, getSidebarByRole]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Gọi API logout để invalidate refresh token ở server
        await caller(
          () =>
            api.post(`/api/v1/auths/sessions/logout`, {
              refreshToken: refreshToken,
            }),
          {
            loadingKey: 'logout-loading',
            successMessage: Messages.LOGOUT_SUCCESS,
          },
        );
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Vẫn tiếp tục clear data ở client dù API call thất bại
    } finally {
      // Luôn clear data ở client sau khi logout
      await clearData();
    }
  }, [api, caller, clearData]);

  const logoutAllDevices = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Gọi API logout để invalidate refresh token ở server
        await caller(
          () =>
            api.post(`/api/v1/auths/sessions/logout-all`, {
              refreshToken: refreshToken,
            }),
          {
            loadingKey: 'logout-all-devices',
            successMessage: Messages.LOGOUT_SUCCESS,
          },
        );
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Vẫn tiếp tục clear data ở client dù API call thất bại
    } finally {
      // Luôn clear data ở client sau khi logout
      await clearData();
    }
  }, [api, caller, clearData]);

  const getUserDetail = useCallback(async () => {
    const { content, succeeded } = await caller(() => api.get(`/api/v1/admin/users/me/detail`), {
      loadingKey: 'get-user-detail',
    });
    if (succeeded) {
      const userDetail = await attachAvatarUrl(api, caller, content);
      dispatch(
        setUserDetailAction({
          userDetail,
        }),
      );
    }
  }, [caller, api, dispatch]);

  const getData2fa = useCallback(async () => {
    const { content, succeeded } = await caller(() => api.get(`/api/v1/admin/users/me/2fa`), {
      loadingKey: 'get-2fa',
    });
    if (succeeded) {
      dispatch(
        setData2faAction({
          data2fa: content,
        }),
      );
    }
  }, [caller, api, dispatch]);

  const updateMyProfile = useCallback(
    async (payload: UpdateMyProfilePayload) => {
      const response = await caller(() => api.patch(`/api/v1/admin/users/me/profile`, payload), {
        loadingKey: 'update-my-profile',
        successMessage: 'Profile updated successfully.',
      });

      if (!response?.succeeded) return false;

      await getUserDetail();
      await globalFetchProfile(api, caller, dispatch, null);
      return true;
    },
    [api, caller, dispatch, getUserDetail],
  );

  return {
    login,
    loginWith2FA,
    logout,
    logoutAllDevices,
    clearData,
    checkAuthStatus,
    isAuthenticated,
    isLoading,
    forgotPasswordData,
    fetchProfile,
    forgotPassword,
    resendForgotPassword,
    verifyForgotPassword,
    resetPassword,
    changePassword,
    getUserDetail,
    updateMyProfile,
    getData2fa,
  };
};

const globalRefreshToken = async (_api?: any, _caller?: any) => refreshAccessToken();

export { globalRefreshToken, globalFetchProfile };
